import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import translations from '../utils/translations.json';
import { useMediaQuery } from "@chakra-ui/react";
import { MdMenu, MdDashboard, MdSettings, MdLocalActivity, MdAutorenew, MdArrowBack, MdFace, MdEventAvailable, MdForum, MdKeyboardArrowLeft, MdRemoveRedEye } from "react-icons/md";
import { FaRobot } from "react-icons/fa";
import { RiCopperCoinLine } from "react-icons/ri";
import axios from 'axios';
import { SketchPicker } from 'react-color';
import { colorCombinations } from '../utils/colorCombinations';
import fontList from '../utils/fontList';
import Swal from 'sweetalert2';
import './GrowthPilotSettings.css';

const isValidUrl = (url) => {
    try {
        new URL(url);
    } catch (e) {
        return false;
    }
    return true;
};

const UserProfileImage = ({ userId, perfilPicture }) => {
    const defaultAvatarUrl = `${process.env.REACT_APP_AVATAR_BASE_URL}${userId}${process.env.REACT_APP_AVATAR_PARAMS}`;
    const imageUrl = isValidUrl(perfilPicture) ? perfilPicture : defaultAvatarUrl;
    return (
        <div className="user-profile-image" style={{ backgroundImage: `url(${imageUrl})` }}></div>
    );
};

const GrowthPilotSettings = () => {
    console.log('[GrowthPilotSettings] Iniciando renderização do componente');

    const location = useLocation();
    const navigate = useNavigate();
    const supportedLanguages = ['pt', 'en'];
    const defaultLanguage = 'en';

    // Define o idioma com base no navegador ou retorna ao padrão se não suportado
    const browserLanguage = navigator.language.slice(0, 2);
    const initialLanguage = supportedLanguages.includes(browserLanguage) ? browserLanguage : defaultLanguage;

    const [language, setLanguage] = useState(localStorage.getItem('language') || initialLanguage);
    const t = (screen, key) => translations[language][screen][key];

    // Verificar o parâmetro 'lang' na URL e definir a linguagem com base nele
    const params = new URLSearchParams(location.search);
    const langFromUrl = params.get('lang');
    const userId = localStorage.getItem('userId');
    const accessToken = localStorage.getItem('accessToken');
    const totenId = localStorage.getItem('totenId');

    const [isMobile] = useMediaQuery('(max-width: 768px)');
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    // Estados para configurações
    const [activeSettingsTab, setActiveSettingsTab] = useState('general');

    // Estados para seleção de cores e fontes
    const [styleConfig, setStyleConfig] = useState({
        colors: {
            Titulos: '#FFFFFF',
            Textos: '#FFFFFF',
            Fundo: '#000000'
        },
        fonts: {
            titleFont: fontList[0]?.value || '',
            textFont: fontList[0]?.value || ''
        },
        useCustomStyle: false,
        comboId: colorCombinations[0]?.comboId || null,
        styleOption: 'templates',
        aspectRatio: '1:1'
    });

    const [showColorPicker, setShowColorPicker] = useState(false);
    const [selectedColorType, setSelectedColorType] = useState(null);
    const [saveAsDefault, setSaveAsDefault] = useState(false);
    const [fontsReady, setFontsReady] = useState(false);
    
    // Estados para preview como no styleCustomization.js
    const [previewColors, setPreviewColors] = useState({
        Titulos: '#FFFFFF',
        Textos: '#FFFFFF',
        Fundo: '#000000'
    });
    const [fonts, setFonts] = useState({
        titleFont: fontList[0]?.value || '',
        textFont: fontList[0]?.value || ''
    });

    // Estados para templates
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [currentTemplateSlides, setCurrentTemplateSlides] = useState({});
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    const [respostaPublicaPadrao, setRespostaPublicaPadrao] = useState('');
    const [mensagemPrivadaPadrao, setMensagemPrivadaPadrao] = useState('');
    const [link, setLink] = useState('');
    const [textoBotao, setTextoBotao] = useState('');
    const [description, setDescription] = useState('');
    const [ctaPadrao, setCtaPadrao] = useState('');
    const [horariosPadrao, setHorariosPadrao] = useState([]);
    const [horarioPadrao, setHorarioPadrao] = useState('');
    const [textoPersonalizado, setTextoPersonalizado] = useState('');
    const [isAutomatico, setIsAutomatico] = useState(true);
    const [isGrowthPilotEnabled, setIsGrowthPilotEnabled] = useState(false);
    const [timezone, setTimezone] = useState('America/Sao_Paulo');

    const TIMEZONES = [
        { label: 'America/São Paulo (Brasil)', value: 'America/Sao_Paulo' },
        { label: 'America/Bogota (Colômbia)', value: 'America/Bogota' },
        { label: 'America/Cidade do México (México)', value: 'America/Mexico_City' },
        { label: 'America/Novo Iorque (EUA)', value: 'America/New_York' },
        { label: 'Europe/Lisboa (Portugal)', value: 'Europe/Lisbon' },
        { label: 'Europe/Madrid (Espanha)', value: 'Europe/Madrid' },
        { label: 'Europe/Roma (Itália)', value: 'Europe/Rome' },
        { label: 'UTC', value: 'UTC' }
    ];

    useEffect(() => {
        if (langFromUrl && langFromUrl !== language) {
            setLanguage(langFromUrl);
            localStorage.setItem('language', langFromUrl);
        }
    }, [langFromUrl, language]);

    // Carregar templates quando o componente é montado
    useEffect(() => {
        fetchTemplates();
    }, []);

    // Carregar configurações quando o componente é montado
    useEffect(() => {
        loadProfileSettings();
    }, []);

    // Carregar todas as fontes do Google Fonts no início
    useEffect(() => {
        const loadFonts = async () => {
            try {
                const validFonts = fontList.filter(font => font.value);
                const fontFamilies = validFonts
                    .map(font => `family=${font.value.replace(/ /g, '+')}:wght@400;700`)
                    .join('&');

                const url = `https://fonts.googleapis.com/css2?${fontFamilies}&display=swap`;

                const existingLink = document.querySelector(`link[href^="https://fonts.googleapis.com/css2"]`);
                if (existingLink) {
                    existingLink.href = url;
                } else {
                    const link = document.createElement('link');
                    link.href = url;
                    link.rel = 'stylesheet';
                    document.head.appendChild(link);
                }

                await document.fonts.ready;

                validFonts.forEach(font => {
                    const isLoaded = document.fonts.check(`12px "${font.value}"`);
                });

            } catch (error) {
                console.error('Erro ao carregar fontes:', error);
            }
        };

        loadFonts();
    }, [fontList]);

    const handleHamburguerClick = () => {
        if (isMobile) {
            setIsSidebarOpen(prev => !prev);
        }
    };

    const handleSidebarToggle = () => {
        setIsSidebarCollapsed(prev => !prev);
    };

    const handleLanguageChange = (newLanguage) => {
        if (newLanguage !== language) {
            setLanguage(newLanguage);
            localStorage.setItem('language', newLanguage);
            navigate(`${location.pathname}?lang=${newLanguage}`);
        }
    };

    const perfilOpen = () => {
        navigate(`/viewuser?lang=${language}&from=growthpilot`);
    }

    const historyOpen = (language) => {
        navigate(`/history?lang=${language}&from=growthpilot`);
    }

    const logout = (language) => {
        navigate(`/login?lang=${language}`);
    }

    const mainOpen = (language) => {
        navigate(`/main?lang=${language}`);
    }

    const pciOpen = (language) => {
        navigate(`/pci?lang=${language}&from=growthpilot`);
    }

    const automacaoOpen = (language) => {
        navigate(`/automacao?lang=${language}&from=growthpilot`);
    }

    const growthPilotOpen = (language) => {
        navigate(`/growthpilot?lang=${language}`);
    }

    // Funções para manipulação de cores e fontes
    const handleColorSelection = (colors, comboId) => {
        const newColors = {
            Titulos: colors[0],
            Textos: colors[1],
            Fundo: colors[2]
        };

        setStyleConfig(prev => ({
            ...prev,
            colors: newColors,
            comboId: comboId,
            useCustomStyle: false
        }));
    };

    const openColorPicker = (colorType) => {
        setSelectedColorType(colorType);
        setShowColorPicker(true);
    };

    const handleColorChange = (color) => {
        const newColors = {
            ...previewColors,
            [selectedColorType]: color.hex
        };

        setPreviewColors(newColors);
        setStyleConfig(prev => ({
            ...prev,
            colors: newColors,
            useCustomStyle: true
        }));
    };

    const handleFontChange = async (type, value) => {
        if (!value) return;

        // Verificar se a fonte está disponível
        const isAvailable = document.fonts.check(`12px "${value}"`);

        if (!isAvailable) {
            const link = document.createElement('link');
            link.href = `https://fonts.googleapis.com/css2?family=${value.replace(/ /g, '+')}:wght@400;700&display=swap`;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        const newFonts = {
            ...fonts,
            [type]: value
        };

        setFonts(newFonts);
        setStyleConfig(prev => ({
            ...prev,
            fonts: newFonts,
            useCustomStyle: true
        }));

        // Forçar re-renderização do PreviewBox
        setFontsReady(false);
        setTimeout(() => setFontsReady(true), 50);
    };

    const handleSaveDefault = async (e) => {
        setSaveAsDefault(e.target.checked);
        if (e.target.checked) {
            try {
                // Primeiro, salvar o texto personalizado no toten para processar com IA
                if (textoPersonalizado && textoPersonalizado.trim()) {
                    // Verificar limite de caracteres
                    if (textoPersonalizado.length > 2000) {
                        Swal.fire({
                            icon: 'warning',
                            title: 'Limite de caracteres excedido',
                            text: 'O texto não pode ter mais de 2000 caracteres.',
                            customClass: {
                                popup: 'my-swal-special',
                                title: 'myCustomTitle'
                            }
                        });
                        return;
                    }
                    // Buscar o toten atual para manter os dados existentes
                    const profileResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users/profiles/${userId}`);
                    const profileData = profileResponse.data;
                    
                    if (profileData && profileData.length > 0 && profileData[0].totens) {
                        const currentToten = profileData[0].totens.find(t => t.toten === totenId);
                        
                        if (currentToten) {
                            // Criar objeto toten com dados existentes + nova descrição
                            const totenUpdate = {
                                ...currentToten,
                                description: textoPersonalizado
                            };
                            
                            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/users/profiles/save`, {
                                userId: userId,
                                toten: totenUpdate,
                                firstAccess: false
                            });
                        }
                    }
                }

                const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/calendar/save-profile-settings`, {
                    userId: userId,
                    totenId: totenId,
                    colors: styleConfig.colors,
                    titleFont: styleConfig.fonts.titleFont,
                    textFont: styleConfig.fonts.textFont,
                    templateTamanho: styleConfig.aspectRatio,
                    templateTipo: selectedTemplate,
                    description: textoPersonalizado,
                    characteristic: 'teste',
                    ctaPadrao: ctaPadrao,
                    respostaPublicaPadrao: respostaPublicaPadrao,
                    mensagemPrivadaPadrao: mensagemPrivadaPadrao,
                    link: link,
                    textoBotao: textoBotao,
                    horariosPadrao: [horarioPadrao],
                    isAutomatico: isAutomatico,
                    isGrowthPilotEnabled: isGrowthPilotEnabled,
                    timezone: timezone
                });

                if (response.data.success) {
                    localStorage.setItem('customColors', JSON.stringify(styleConfig.colors));
                    localStorage.setItem('customFonts', JSON.stringify(styleConfig.fonts));
                    localStorage.setItem('useCustomStyle', 'true');
                    
                    Swal.fire({
                        icon: 'success',
                        title: 'Sucesso',
                        text: 'Configurações salvas com sucesso!',
                        customClass: {
                            popup: 'my-swal-special',
                            title: 'myCustomTitle'
                        }
                    });
                } else {
                    console.error('Erro ao salvar configurações:', response.data.message);
                }
            } catch (error) {
                console.error('Erro ao salvar configurações:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Erro',
                    text: 'Erro ao salvar configurações',
                    customClass: {
                        popup: 'my-swal-special',
                        title: 'myCustomTitle'
                    }
                });
            }
        } else {
            localStorage.removeItem('customColors');
            localStorage.removeItem('customFonts');
            localStorage.setItem('useCustomStyle', 'false');
        }
    };

    // Funções para gerenciar templates
    const fetchTemplates = async () => {
        try {
            setLoadingTemplates(true);
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/templates`);

            const sortedTemplates = response.data.sort((a, b) => {
                if (a.name === "PostSucesso") return -1;
                if (b.name === "PostSucesso") return 1;
                return 0;
            });

            setTemplates(sortedTemplates);

            // Inicializa o estado dos slides atuais
            const initialSlides = {};
            sortedTemplates.forEach(template => {
                initialSlides[template.id] = 0;
            });
            setCurrentTemplateSlides(initialSlides);

            // Seleciona automaticamente o primeiro template se nenhum estiver selecionado
            if (sortedTemplates.length > 0 && !selectedTemplate) {
                setSelectedTemplate(sortedTemplates[0].title);
            }
        } catch (error) {
            console.error('Erro ao buscar templates:', error);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleTemplateSelect = (templateName) => {
        setSelectedTemplate(templateName);
    };

    const nextTemplateSlide = (templateId) => {
        setCurrentTemplateSlides(prev => {
            const currentIndex = prev[templateId] || 0;
            const template = templates.find(t => t.id === templateId);
            const totalSlides = template?.slides?.length || 1;
            return {
                ...prev,
                [templateId]: (currentIndex + 1) % totalSlides
            };
        });
    };

    const prevTemplateSlide = (templateId) => {
        setCurrentTemplateSlides(prev => {
            const currentIndex = prev[templateId] || 0;
            const template = templates.find(t => t.id === templateId);
            const totalSlides = template?.slides?.length || 1;
            return {
                ...prev,
                [templateId]: (currentIndex - 1 + totalSlides) % totalSlides
            };
        });
    };

    const saveGrowthPilotToggle = async (enabled) => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/calendar/save-profile-settings`, {
                userId: userId,
                totenId: totenId,
                colors: styleConfig.colors,
                titleFont: styleConfig.fonts.titleFont,
                textFont: styleConfig.fonts.textFont,
                templateTamanho: styleConfig.aspectRatio,
                templateTipo: selectedTemplate,
                description: textoPersonalizado,
                characteristic: 'teste',
                ctaPadrao: ctaPadrao,
                respostaPublicaPadrao: respostaPublicaPadrao,
                mensagemPrivadaPadrao: mensagemPrivadaPadrao,
                link: link,
                textoBotao: textoBotao,
                horariosPadrao: [horarioPadrao],
                isAutomatico: isAutomatico,
                isGrowthPilotEnabled: enabled,
                timezone: timezone
            });

            if (response.data.success) {
                console.log('GrowthPilot toggle salvo com sucesso');
                // Feedback visual discreto
                Swal.fire({
                    icon: 'success',
                    title: 'Configuração salva',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                    customClass: {
                        popup: 'my-swal-special',
                        title: 'myCustomTitle'
                    }
                });
            }
        } catch (error) {
            console.error('Erro ao salvar GrowthPilot toggle:', error);
            // Feedback visual de erro
            Swal.fire({
                icon: 'error',
                title: 'Erro ao salvar',
                text: 'Não foi possível salvar a configuração',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });
        }
    };

    const loadProfileSettings = async () => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/calendar/get-profile-settings`, {
                userId: userId,
                totenId: totenId
            });

            if (response.data.success && response.data.settings) {
                const settings = response.data.settings;
                
                // Preencher os campos da aba Geral
                setHorarioPadrao(settings.horariosPadrao?.[0] || '');
                setCtaPadrao(settings.ctaPadrao || '');
                setTextoPersonalizado(settings.description || '');
                setIsAutomatico(settings.isAutomatico !== undefined ? settings.isAutomatico : true);
                setIsGrowthPilotEnabled(settings.isGrowthPilotEnabled !== undefined ? settings.isGrowthPilotEnabled : false);

                // Preencher os campos da aba Automação
                setRespostaPublicaPadrao(settings.respostaPublicaPadrao || '');
                setMensagemPrivadaPadrao(settings.mensagemPrivadaPadrao || '');
                setLink(settings.link || '');
                setTextoBotao(settings.textoBotao || '');

                // Preencher os campos de estilo
                if (settings.colors) {
                    setStyleConfig(prev => ({
                        ...prev,
                        colors: settings.colors
                    }));
                    setPreviewColors(settings.colors);
                }
                if (settings.titleFont || settings.textFont) {
                    const savedFonts = {
                        titleFont: settings.titleFont || '',
                        textFont: settings.textFont || ''
                    };
                    setStyleConfig(prev => ({
                        ...prev,
                        fonts: savedFonts
                    }));
                    setFonts(savedFonts);
                }

                // Fuso horário
                if (settings.timezone) {
                    setTimezone(settings.timezone);
                } else {
                    setTimezone('America/Sao_Paulo');
                }

                // Preencher o template selecionado
                if (settings.templateTipo) {
                    setSelectedTemplate(settings.templateTipo);
                }
            }
        } catch (error) {
            console.error('Erro ao carregar configurações:', error);
        }
    };

    // Componente de preview das cores
    const PreviewBox = ({ colors, fonts, boxHeight }) => {
        const [fontsLoaded, setFontsLoaded] = useState(false);

        useEffect(() => {
            const checkFontsLoaded = async () => {
                if (fonts.titleFont && fonts.textFont) {
                    // Esperar um momento para as fontes carregarem
                    await new Promise(resolve => setTimeout(resolve, 100));

                    const titleLoaded = document.fonts.check(`12px "${fonts.titleFont}"`);
                    const textLoaded = document.fonts.check(`12px "${fonts.textFont}"`);

                    setFontsLoaded(titleLoaded && textLoaded);
                }
            };

            checkFontsLoaded();
        }, [fonts.titleFont, fonts.textFont, fontsReady]);

        const titleStyle = {
            color: colors.Titulos,
            fontFamily: fonts.titleFont ? `"${fonts.titleFont}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : 'system-ui, sans-serif',
            margin: 0,
            fontWeight: '700'
        };
        const textStyle = {
            color: colors.Textos,
            fontFamily: fonts.textFont ? `"${fonts.textFont}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : 'system-ui, sans-serif',
            margin: 0,
            fontWeight: '400'
        };
        return (
            <div style={{
                padding: '20px',
                borderRadius: '8px',
                backgroundColor: colors.Fundo,
                border: '1px solid #e2e8f0',
                marginBottom: '20px',
                minHeight: '95px',
                height: '95px',
                transition: 'height 0.2s',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <h4 style={titleStyle}>
                        Título do Post
                    </h4>
                    <MdRemoveRedEye style={{ color: colors.Titulos }} />
                </div>
                <p style={textStyle}>
                    Este é um exemplo de como ficará o texto do seu carrossel com as cores e fontes selecionadas.
                </p>
            </div>
        );
    };

    // Componente de grid de templates
    const TemplateGrid = ({ isPostEditorContext }) => {
        if (loadingTemplates) {
            return (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    Carregando templates...
                </div>
            );
        }

        if (templates.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    Nenhum template disponível
                </div>
            );
        }

        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '28px',
                marginTop: '15px',
                padding: '8px 0'
            }}>
                {templates.map(template => {
                    const isSelected = selectedTemplate === template.title;
                    const currentSlide = currentTemplateSlides[template.id] || 0;

                    return (
                        <div key={template.id} style={{
                            background: '#fff',
                            border: isSelected ? '2.5px solid #8E59FF' : '1.5px solid #e2e8f0',
                            borderRadius: '20px',
                            boxShadow: '0 4px 18px 0 rgba(60,60,60,0.07)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            minHeight: '320px',
                            maxWidth: '270px',
                            margin: '0 auto',
                            cursor: 'pointer',
                            transition: 'border 0.2s, box-shadow 0.2s',
                            padding: 0
                        }}
                            onClick={() => handleTemplateSelect(template.title)}
                        >
                            <div style={{ width: '100%', aspectRatio: '1/1', position: 'relative', background: '#f7f7f7', overflow: 'visible', zIndex: 10 }}>
                                <img
                                    src={template.slides[currentSlide]?.thumbnailUrl}
                                    alt={`Template ${template.title}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                        borderRadius: 0
                                    }}
                                />
                                {/* Indicador de slides (bolinhas) */}
                                <div style={{
                                    position: 'absolute',
                                    left: '50%',
                                    bottom: '12px',
                                    transform: 'translateX(-50%)',
                                    display: 'flex',
                                    gap: '5px',
                                    zIndex: 3
                                }}>
                                    {template.slides.map((slide, idx) => (
                                        <span
                                            key={idx}
                                            onClick={e => {
                                                e.stopPropagation();
                                                setCurrentTemplateSlides(prev => ({ ...prev, [template.id]: idx }));
                                            }}
                                            style={{
                                                width: '7px',
                                                height: '7px',
                                                borderRadius: '50%',
                                                background: idx === currentSlide ? '#8E59FF' : '#fff',
                                                border: '1.2px solid #8E59FF',
                                                display: 'inline-block',
                                                transition: 'background 0.2s',
                                                boxShadow: idx === currentSlide ? '0 0 2px #8E59FF' : 'none',
                                                cursor: 'pointer'
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div style={{
                                width: '100%',
                                padding: '28px 0 18px 0',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                flex: 1
                            }}>
                                <span style={{
                                    fontSize: '14px',
                                    color: '#757575',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    margin: 0,
                                    lineHeight: 1.2
                                }}>{template.name}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Componente ColorPalette simplificado
    const ColorPalette = ({ onCombinationSelect, selectedComboId }) => {
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                {colorCombinations.slice(0, 12).map((combo, index) => (
                    <div
                        key={combo.comboId}
                        style={{
                            width: '60px',
                            height: '60px',
                            cursor: 'pointer',
                            borderRadius: '8px',
                            border: selectedComboId === combo.comboId ? '3px solid #8E59FF' : '2px solid #ddd',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        }}
                        onClick={() => onCombinationSelect(combo.colors, combo.comboId)}
                    >
                        <div style={{ backgroundColor: combo.colors[0], height: '20px' }}></div>
                        <div style={{ backgroundColor: combo.colors[1], height: '20px' }}></div>
                        <div style={{ backgroundColor: combo.colors[2], height: '20px' }}></div>
                    </div>
                ))}
            </div>
        );
    };

    // Renderizar a sidebar fixa
    const renderSidebar = () => {
        return (
            <div className={`sidebar-fixed-settings ${isMobile && isSidebarOpen ? 'active' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-header-settings">
                    <div className="sidebar-logo">
                        <FaRobot size={22} />
                        <span className="menu-text-settings">GrowthPilot</span>
                    </div>
                    <div className="sidebar-toggle-btn" onClick={handleSidebarToggle}>
                        <MdKeyboardArrowLeft size={24} />
                    </div>
                </div>

                <div className="sidebar-menu">
                    <div className="menu-item-settings" onClick={() => growthPilotOpen(language)}>
                        <MdEventAvailable size={20} />
                        <span className="menu-text-settings">Content Planner</span>
                    </div>

                    <div className="menu-item-settings" onClick={() => pciOpen(language)}>
                        <RiCopperCoinLine size={20} />
                        <span className="menu-text-settings">PCI</span>
                    </div>

                    <div className="menu-item-settings" onClick={() => perfilOpen()}>
                        <MdFace size={20} />
                        <span className="menu-text-settings">Avatares</span>
                    </div>

                    {/* <div className="menu-item-settings" onClick={() => historyOpen(language)}>
                        <MdForum size={20} />
                        <span className="menu-text-settings">Histórico de Conteúdo</span>
                    </div> */}

                    <div className="menu-item-settings" onClick={() => automacaoOpen(language)}>
                        <MdAutorenew size={20} />
                        <span className="menu-text-settings">Automação</span>
                    </div>

                    <div className="menu-item-settings" onClick={() => navigate(`/growthpilot/analytics?lang=${language}`)}>
                        <MdDashboard size={20} />
                        <span className="menu-text-settings">Analytics</span>
                    </div>

                    <div className="menu-item-settings active">
                        <MdSettings size={20} />
                        <span className="menu-text-settings">Configurações</span>
                    </div>

                    <div className="menu-item-settings" onClick={() => mainOpen(language)}>
                        <MdArrowBack size={20} />
                        <span className="menu-text-settings">Voltar para o PostSucesso</span>
                    </div>
                </div>

                {/* Botão de suporte na parte inferior */}
                <div className="sidebar-footer">
                    <div className="support-button-settings">
                        <MdLocalActivity size={22} />
                        <span className="menu-text-settings">Suporte</span>
                    </div>
                </div>

            </div>
        );
    };

    return (
        <div className="growth-pilot">
            {/* Sidebar fixa no lado esquerdo */}
            {renderSidebar()}

            {/* Conteúdo principal */}
            <div className={`main-content ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                {/* Topo: Título e Foto do Usuário */}
                <div className="growthpilot-topbar-settings">
                    {isMobile && (
                        <div className="mobile-menu-toggle" onClick={handleHamburguerClick}>
                            <MdMenu size={28} />
                        </div>
                    )}
                    <div className="growthpilot-title-container">
                        <div className="growthpilot-title">
                            <MdSettings size={24} className="title-icon" />
                            Configurações
                        </div>
                    </div>
                    <div className="topbar-actions">
                        <div className="growthpilot-user growthpilot-user-large-settings">
                            <UserProfileImage userId={userId} perfilPicture={localStorage.getItem('perfil_picture')} />
                        </div>
                    </div>
                </div>

                {/* Container principal com alinhamento consistente */}
                <div className="growthpilot-grid-container">
                    {/* Área destinada ao conteúdo das configurações */}
                    <div className="growthpilot-calendar-area" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <div className="settings-container" style={{ flex: 1, overflowY: 'auto', width: '100%', maxWidth: 'none' }}>
                            <div className="settings-tabs" style={{ display: 'flex', width: '100%', justifyContent: 'stretch', gap: 50 }}>
                                <button
                                    className={`tab ${activeSettingsTab === 'general' ? 'active' : ''}`}
                                    onClick={() => setActiveSettingsTab('general')}
                                    style={{ flex: 1, borderRadius: 8 }}
                                >
                                    Geral
                                </button>
                                <button
                                    className={`tab ${activeSettingsTab === 'style' ? 'active' : ''}`}
                                    onClick={() => setActiveSettingsTab('style')}
                                    style={{ flex: 1, borderRadius: 8 }}
                                >
                                    Estilo e Cores
                                </button>
                                <button
                                    className={`tab ${activeSettingsTab === 'automation' ? 'active' : ''}`}
                                    onClick={() => setActiveSettingsTab('automation')}
                                    style={{ flex: 1, borderRadius: 8 }}
                                >
                                    Automação
                                </button>
                            </div>

                            <div className="settings-content">
                                {activeSettingsTab === 'general' && (
                                    <div className="tab-content" style={{ marginTop: -50 }}>
                                        <h3>Configurações Gerais</h3>
                                        
                                        {/* Linha única com os três controles */}
                                        <div className="general-controls-row">
                                            {/* Ativar GrowthPilot */}
                                            <div className="general-control-item">
                                                <label className="general-control-label">Ativar GrowthPilot:</label>
                                                <div className="general-control-toggle-container">
                                                    <label className="general-control-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={isGrowthPilotEnabled}
                                                            onChange={(e) => {
                                                                setIsGrowthPilotEnabled(e.target.checked);
                                                                saveGrowthPilotToggle(e.target.checked);
                                                            }}
                                                        />
                                                        <span className={`general-control-toggle-slider ${isGrowthPilotEnabled ? 'active' : ''}`}>
                                                            <span className={`general-control-toggle-thumb ${isGrowthPilotEnabled ? 'active' : ''}`}></span>
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>

                                            {/* Horário padrão de postagem */}
                                            <div className="general-control-item">
                                                <label className="general-control-label">Horário padrão de postagem:</label>
                                                <input
                                                    type="time"
                                                    value={horarioPadrao}
                                                    onChange={(e) => setHorarioPadrao(e.target.value)}
                                                    className="general-control-time-input"
                                                />
                                            </div>

                                            {/* Fuso horário */}
                                            <div className="general-control-item">
                                                <label className="general-control-label">Fuso horário:</label>
                                                <select
                                                    value={timezone}
                                                    onChange={(e) => setTimezone(e.target.value)}
                                                    className="general-control-select"
                                                    style={{ minWidth: 220 }}
                                                >
                                                    {TIMEZONES.map(tz => (
                                                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Modo Automação */}
                                            <div className="general-control-item">
                                                <label className="general-control-label">Modo Automação:</label>
                                                <div className="general-control-radio-group">
                                                    <label className="general-control-radio-label">
                                                        <input
                                                            type="radio"
                                                            name="modoAutomacao"
                                                            value="true"
                                                            checked={isAutomatico === true}
                                                            onChange={() => setIsAutomatico(true)}
                                                            style={{ accentColor: '#8E59FF' }}
                                                        />
                                                        100% Automático
                                                    </label>
                                                    <label className="general-control-radio-label">
                                                        <input
                                                            type="radio"
                                                            name="modoAutomacao"
                                                            value="false"
                                                            checked={isAutomatico === false}
                                                            onChange={() => setIsAutomatico(false)}
                                                            style={{ accentColor: '#8E59FF' }}
                                                        />
                                                        Semi-automático
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label style={{ fontSize: '14px', color: '#757575', marginBottom: '12px', display: 'block', fontWeight: 600 }}>CTA padrão:</label>
                                            <input
                                                type="text"
                                                value={ctaPadrao}
                                                onChange={(e) => setCtaPadrao(e.target.value)}
                                                placeholder="Insira seu CTA padrão..."
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    border: '1px solid #e2e8f0',
                                                    backgroundColor: 'white',
                                                    borderRadius: '5px',
                                                    fontSize: '15px',
                                                    color: '#757575',
                                                    marginBottom: '7.5px'
                                                }}
                                            />
                                        </div>
                                       
                                        <div className="form-group">
                                            <label style={{ fontSize: '14px', color: '#757575', marginBottom: '12px', display: 'block', fontWeight: 600 }}>Personalização de texto:</label>
                                            <textarea
                                                rows={2}
                                                value={textoPersonalizado}
                                                onChange={(e) => setTextoPersonalizado(e.target.value)}
                                                placeholder="Insira aqui seu texto personalizado..."
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    border: '1px solid #e2e8f0',
                                                    backgroundColor: 'white',
                                                    borderRadius: '5px',
                                                    fontSize: '15px',
                                                    maxHeight: '100px',
                                                    color: '#757575',
                                                    marginBottom: '0px',
                                                    resize: 'vertical'
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {activeSettingsTab === 'style' && (
                                    <div className="tab-content" style={{ marginTop: -50 }}>
                                        <h3>Estilo e Cores</h3>
                                        {/* Reutilizar o conteúdo da aba de estilo existente */}
                                        <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', alignItems: 'center' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '14px', color: '#757575' }}>
                                                <input
                                                    type="radio"
                                                    name="styleOption"
                                                    value="templates"
                                                    checked={styleConfig.styleOption === 'templates'}
                                                    onChange={() => setStyleConfig(prev => ({ ...prev, styleOption: 'templates' }))}
                                                    style={{ accentColor: '#8E59FF' }}
                                                />
                                                Templates
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '14px', color: '#757575' }}>
                                                <input
                                                    type="radio"
                                                    name="styleOption"
                                                    value="personalizado"
                                                    checked={styleConfig.styleOption === 'personalizado'}
                                                    onChange={() => setStyleConfig(prev => ({ ...prev, styleOption: 'personalizado' }))}
                                                    style={{ accentColor: '#8E59FF' }}
                                                />
                                                Personalizado
                                            </label>
                                        </div>
                                        {styleConfig.styleOption === 'templates' && (
                                            <div style={{ marginTop: '10px' }}>
                                                {selectedTemplate && (
                                                    <div style={{
                                                        padding: '12px',
                                                        backgroundColor: '#f7fafc',
                                                        borderRadius: '8px',
                                                        marginBottom: '15px',
                                                        border: '1px solid #e2e8f0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: '24px'
                                                    }}>
                                                        <div style={{ fontSize: '14px', color: '#757575', fontWeight: '600' }}>
                                                            {selectedTemplate}
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '18px' }}>
                                                            <span style={{ fontWeight: 600, color: '#757575', fontSize: '14px' }}>Tamanho:</span>
                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500, fontSize: '14px', color: '#757575' }}>
                                                                <input
                                                                    type="radio"
                                                                    name="templateAspect"
                                                                    value="1:1"
                                                                    checked={styleConfig.aspectRatio === '1:1'}
                                                                    onChange={() => setStyleConfig(prev => ({ ...prev, aspectRatio: '1:1' }))}
                                                                    style={{ accentColor: '#8E59FF' }}
                                                                />
                                                                (1:1) Quadrado
                                                            </label>
                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500, fontSize: '14px', color: '#757575' }}>
                                                                <input
                                                                    type="radio"
                                                                    name="templateAspect"
                                                                    value="4:5"
                                                                    checked={styleConfig.aspectRatio === '4:5'}
                                                                    onChange={() => setStyleConfig(prev => ({ ...prev, aspectRatio: '4:5' }))}
                                                                    style={{ accentColor: '#8E59FF' }}
                                                                />
                                                                (4:5) Retrato
                                                            </label>
                                                        </div>
                                                    </div>
                                                )}
                                                <TemplateGrid isPostEditorContext={false}/>
                                            </div>
                                        )}
                                        {styleConfig.styleOption === 'personalizado' && (
                                            <>
                                                <PreviewBox colors={previewColors} fonts={fonts} boxHeight="70px" />
                                                <div style={{ display: 'flex', flexDirection: 'row', gap: '32px', alignItems: 'flex-start', marginTop: '20px' }}>
                                                    {/* Cores Personalizadas */}
                                                    <div style={{ minWidth: '180px' }}>
                                                        <label style={{ fontSize: '14px', color: '#757575', marginBottom: '18px', display: 'block', fontWeight: 600 }}>
                                                            Cores Personalizadas
                                                        </label>
                                                        <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
                                                            {['Titulos', 'Textos', 'Fundo'].map((colorType) => (
                                                                <div key={colorType} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                                                                    <span style={{ fontSize: '12px', color: '#757575' }}>{colorType}</span>
                                                                    <div
                                                                        style={{
                                                                            width: '48px',
                                                                            height: '48px',
                                                                            borderRadius: '6px',
                                                                            border: '1px solid #ddd',
                                                                            cursor: 'pointer',
                                                                            backgroundColor: previewColors[colorType],
                                                                            transition: 'transform 0.2s'
                                                                        }}
                                                                        onClick={() => openColorPicker(colorType)}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    {/* Fontes, uma embaixo da outra */}
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', flex: 1 }}>
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '14px', color: '#757575', marginBottom: '18px', fontWeight: 600 }}>
                                                                Fonte do Título
                                                            </label>
                                                            <select
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px',
                                                                    border: '1px solid #e2e8f0',
                                                                    backgroundColor: 'white',
                                                                    borderRadius: '5px',
                                                                    fontSize: '15px',
                                                                    color: '#757575'
                                                                }}
                                                                value={fonts.titleFont || ''}
                                                                onChange={(e) => handleFontChange('titleFont', e.target.value)}
                                                            >
                                                                <option value="" disabled>Selecionar fonte</option>
                                                                {fontList.filter(font => font.value).map(font => (
                                                                    <option key={font.value} value={font.value}>
                                                                        {font.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '14px', color: '#757575', marginBottom: '18px', fontWeight: 600 }}>
                                                                Fonte do Texto
                                                            </label>
                                                            <select
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px',
                                                                    border: '1px solid #e2e8f0',
                                                                    backgroundColor: 'white',
                                                                    borderRadius: '5px',
                                                                    fontSize: '15px',
                                                                    color: '#757575'
                                                                }}
                                                                value={fonts.textFont || ''}
                                                                onChange={(e) => handleFontChange('textFont', e.target.value)}
                                                            >
                                                                <option value="" disabled>Selecionar fonte</option>
                                                                {fontList.filter(font => font.value).map(font => (
                                                                    <option key={font.value} value={font.value}>
                                                                        {font.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {activeSettingsTab === 'automation' && (
                                    <div className="tab-content" style={{ marginTop: -50 }}>
                                        <h3>Configurações de Automação</h3>
                                        <div className="form-group">
                                            <label style={{ fontSize: '14px', color: '#757575', marginBottom: '12px', display: 'block', fontWeight: 600 }}>Resposta Pública Padrão:</label>
                                            <input
                                                type="text"
                                                value={respostaPublicaPadrao}
                                                onChange={(e) => setRespostaPublicaPadrao(e.target.value)}
                                                placeholder="Insira a resposta pública padrão..."
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    border: '1px solid #e2e8f0',
                                                    backgroundColor: 'white',
                                                    borderRadius: '5px',
                                                    fontSize: '15px',
                                                    color: '#757575',
                                                    marginBottom: '7.5px'
                                                }}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label style={{ fontSize: '14px', color: '#757575', marginBottom: '12px', display: 'block', fontWeight: 600 }}>Mensagem Privada Automática:</label>
                                            <input
                                                type="text"
                                                value={mensagemPrivadaPadrao}
                                                onChange={(e) => setMensagemPrivadaPadrao(e.target.value)}
                                                placeholder="Insira a mensagem privada automática..."
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    border: '1px solid #e2e8f0',
                                                    backgroundColor: 'white',
                                                    borderRadius: '5px',
                                                    fontSize: '15px',
                                                    color: '#757575',
                                                    marginBottom: '7.5px'
                                                }}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label style={{ fontSize: '14px', color: '#757575', marginBottom: '12px', display: 'block', fontWeight: 600 }}>Link Externo:</label>
                                            <input
                                                type="text"
                                                value={link}
                                                onChange={(e) => setLink(e.target.value)}
                                                placeholder="Insira o link externo..."
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    border: '1px solid #e2e8f0',
                                                    backgroundColor: 'white',
                                                    borderRadius: '5px',
                                                    fontSize: '15px',
                                                    color: '#757575',
                                                    marginBottom: '7.5px'
                                                }}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label style={{ fontSize: '14px', color: '#757575', marginBottom: '12px', display: 'block', fontWeight: 600 }}>Texto do Botão:</label>
                                            <input
                                                type="text"
                                                value={textoBotao}
                                                onChange={(e) => setTextoBotao(e.target.value)}
                                                placeholder="Insira o texto do botão..."
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    border: '1px solid #e2e8f0',
                                                    backgroundColor: 'white',
                                                    borderRadius: '5px',
                                                    fontSize: '15px',
                                                    color: '#757575',
                                                    marginBottom: '7.5px'
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Botão de salvar */}
                        <div style={{ width: '100%', textAlign: 'right', marginBottom: '20px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={async () => {
                                    try {
                                        // Primeiro, salvar o texto personalizado no toten para processar com IA
                                        if (textoPersonalizado && textoPersonalizado.trim()) {
                                            // Verificar limite de caracteres
                                            if (textoPersonalizado.length > 2000) {
                                                Swal.fire({
                                                    icon: 'warning',
                                                    title: 'Limite de caracteres excedido',
                                                    text: 'O texto não pode ter mais de 2000 caracteres.',
                                                    customClass: {
                                                        popup: 'my-swal-special',
                                                        title: 'myCustomTitle'
                                                    }
                                                });
                                                return;
                                            }
                                            // Buscar o toten atual para manter os dados existentes
                                            const profileResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users/profiles/${userId}`);
                                            const profileData = profileResponse.data;
                                            
                                            if (profileData && profileData.length > 0 && profileData[0].totens) {
                                                const currentToten = profileData[0].totens.find(t => t.toten === totenId);
                                                
                                                if (currentToten) {
                                                    // Criar objeto toten com dados existentes + nova descrição
                                                    const totenUpdate = {
                                                        ...currentToten,
                                                        description: textoPersonalizado
                                                    };
                                                    
                                                    await axios.post(`${process.env.REACT_APP_API_BASE_URL}/users/profiles/save`, {
                                                        userId: userId,
                                                        toten: totenUpdate,
                                                        firstAccess: false
                                                    });
                                                }
                                            }
                                        }

                                        // Depois salvar as configurações gerais
                                        await handleSaveDefault({ target: { checked: true } });
                                    } catch (error) {
                                        console.error('Erro ao salvar configurações:', error);
                                        Swal.fire({
                                            icon: 'error',
                                            title: 'Erro',
                                            text: 'Erro ao salvar configurações',
                                            customClass: {
                                                popup: 'my-swal-special',
                                                title: 'myCustomTitle'
                                            }
                                        });
                                    }
                                }}
                                style={{
                                    backgroundColor: '#222',
                                    color: '#fff',
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                    marginTop: '-20px'
                                }}
                            >
                                Salvar Configurações
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal do Color Picker */}
            {showColorPicker && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                    }}>
                        <SketchPicker
                            color={previewColors[selectedColorType] || '#000'}
                            onChange={handleColorChange}
                        />
                        <div style={{ marginTop: '15px', textAlign: 'center' }}>
                            <button
                                onClick={() => setShowColorPicker(false)}
                                style={{
                                    backgroundColor: '#1a202c',
                                    color: 'white',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default GrowthPilotSettings;
